<template>
  <v-dialog v-model="dialog.value" persistent max-width="600">
    <template v-slot:activator="{ on: dialogOn }">
      <v-tooltip bottom dark>
        <template v-slot:activator="{ on: tooltipOn }">
          <v-btn rounded color="primary" dark v-on="{ ...dialogOn, ...tooltipOn }" @click="resetProperties">
            <v-icon>mdi-lock-reset</v-icon>
          </v-btn>
        </template>
        <span>Zmiana hasła</span>
      </v-tooltip>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Zmiana hasła użytkownika</span>
      </v-card-title>
      <v-card-text class="mt-4">
        <v-form ref="form">
          <v-text-field
            v-model="form.adminPassword"
            label="Hasło administratora"
            name="adminPassword"
            type="password"
            :error-messages="adminPasswordErrors"
            @input="$v.form.adminPassword.$touch()"
            @blur="$v.form.adminPassword.$touch()"
          />
          <v-text-field
            v-model="form.password"
            label="Nowe hasło"
            name="password"
            hint="Co najmniej 8 znaków, 1 wielka litera i 1 cyfra"
            :type="showPassword ? 'text' : 'password'"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            :error-messages="passwordErrors"
            counter
            @input="$v.form.password.$touch()"
            @blur="$v.form.password.$touch()"
            @click:append="showPassword = !showPassword"
          />
          <v-text-field
            v-model="form.passwordRepeat"
            label="Powtórz nowe hasło"
            name="passwordRepeat"
            :type="showPasswordRepeat ? 'text' : 'password'"
            :append-icon="showPasswordRepeat ? 'mdi-eye' : 'mdi-eye-off'"
            :error-messages="passwordRepeatErrors"
            @input="$v.form.passwordRepeat.$touch()"
            @blur="$v.form.passwordRepeat.$touch()"
            @click:append="showPasswordRepeat = !showPasswordRepeat"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn class="text--secondary" text :disabled="dialog.loading" @click="dialog.value = false">Anuluj</v-btn>
        <v-btn color="primary" text :loading="dialog.loading" @click="create">Zmień hasło</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, sameAs, minLength } from 'vuelidate/lib/validators';
import { createNamespacedHelpers } from 'vuex';
import UsersApi from '@/api/usersApi';
import { passwordRequirements } from '@/plugins/vuelidate/validators';

const { mapActions } = createNamespacedHelpers('users');
const { mapState: mapAccountState } = createNamespacedHelpers('account');

export default {
  mixins: [validationMixin],
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  data: function() {
    return {
      dialog: {
        value: false,
        loading: false
      },
      form: {
        adminPassword: null,
        password: null,
        passwordRepeat: null
      },
      showPassword: false,
      showPasswordRepeat: false
    };
  },
  computed: {
    ...mapAccountState(['token']),
    adminPasswordErrors() {
      const errors = [];
      if (!this.$v.form.adminPassword.$dirty) {
        return errors;
      }
      if (!this.$v.form.adminPassword.required) {
        errors.push('Hasło administratora jest wymagane');
      }
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.form.password.$dirty) {
        return errors;
      }
      if (!this.$v.form.password.required) {
        errors.push('Nowe hasło jest wymagane');
      }
      if (!this.$v.form.password.minLength) {
        errors.push('Wymagane jest minimum 8 znaków');
      }
      if (!this.$v.form.password.passwordRequirements) {
        errors.push('Hasło nie spełnia wymaganych kryteriów: co najmniej 8 znaków, 1 wielka litera i 1 cyfra');
      }
      return errors;
    },
    passwordRepeatErrors() {
      const errors = [];
      if (!this.$v.form.passwordRepeat.$dirty) {
        return errors;
      }
      if (!this.$v.form.passwordRepeat.required) {
        errors.push('Powtórz nowe hasło');
      }
      if (!this.$v.form.passwordRepeat.sameAs) {
        errors.push('Nowe hasła nie zgadzają się');
      }
      return errors;
    },
    formValid() {
      return !this.$v.form.$invalid && !this.$v.form.$pending;
    }
  },
  validations: {
    form: {
      adminPassword: {
        required
      },
      password: {
        required,
        minLength: minLength(8),
        passwordRequirements: passwordRequirements()
      },
      passwordRepeat: {
        required,
        sameAs: sameAs('password')
      }
    }
  },
  methods: {
    ...mapActions(['updateUser']),
    resetProperties() {
      this.form.password = '';
      this.form.passwordRepeat = '';
      this.$v.$reset();
    },
    async create() {
      this.$v.$touch();

      if (this.formValid) {
        this.dialog.loading = true;

        try {
          await new UsersApi().resetPassword(this.form.adminPassword, this.user.id, this.form.password);
        } catch (e) {
          console.error(e);
          // TODO: handle
        }

        this.dialog.loading = false;
        this.dialog.value = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
